import { useRef, useState } from 'react';

import * as styles from '../FirstResponder.module.css';
import { LiveSafetyStatus } from './liveViewModel';
import { useModalA11y } from './useModalA11y';

/*
 * GRAVY-10373 Task 3 — the reason-collecting confirmation for every mode transition that isn't
 * Stop (Stop keeps its own StopConfirmModal). ONE component with a `target` prop rather than a
 * separate GoLiveConfirmModal, per the design choice left open in the brief: OFF->SHADOW and
 * LIVE->SHADOW reuse the exact same reason prompt as SHADOW->LIVE (Go Live), which only adds the
 * cohort list/count and the safety gate. Less code, same rules, same a11y (useModalA11y, same as
 * StopConfirmModal/ConnectedPolicyEditor).
 *
 * Reason rule matches ConnectedPolicyEditor's: `!reason.trim()` blocks submission. Unlike that
 * component (which validates onClick), this one disables the submit button proactively — the
 * brief requires Go Live to be disabled outright, not merely rejected on click.
 *
 * Safety is only ever a gate for `live` — going DOWN to Shadow is the safer action and must
 * never be blocked by an unproven safety state (mirrors ConsoleHeader's Back-to-Shadow rule).
 *
 * GRAVY-10373 I4: the `live` gate requires `state === 'clear'` AND `basis ===
 * 'shadow_zero_action_proof'` specifically — the backend's Go-Live 422 (`safety_not_clear`) is
 * issued unless safety is clear WITH that exact basis. `clear` via `effective_off` proves nothing
 * about a Shadow run and must not unlock submission here (mirrors ConsoleHeader's own gate).
 */

export type ModeReasonTarget = 'shadow' | 'live';

export type ModeReasonCohortClient = { customerId: number; name: string };

type Props = {
  target: ModeReasonTarget;
  cohortClients: ModeReasonCohortClient[];
  safety: LiveSafetyStatus;
  pending: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
};

const safetyLineFor = (safety: LiveSafetyStatus): string => {
  if (safety.state === 'clear') return 'Safety check: clear to go live.';
  if (safety.state === 'unknown')
    return `Safety check: unknown — ${safety.reason}`;
  return `Safety check: breached — ${safety.reasons.join('; ')}`;
};

const ModeReasonModal = ({
  target,
  cohortClients,
  safety,
  pending,
  onConfirm,
  onCancel,
}: Props) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(true, onCancel, dialogRef);
  const [reason, setReason] = useState('');

  const isLive = target === 'live';
  const safetyBlocksSubmit =
    isLive &&
    !(safety.state === 'clear' && safety.basis === 'shadow_zero_action_proof');
  const submitDisabled = !reason.trim() || pending || safetyBlocksSubmit;

  return (
    <div
      className={styles.overlay}
      data-testid="flp-mode-reason-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className={`${styles.modal} ${styles.modalSm}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="flp-mode-reason-title"
        ref={dialogRef}
        tabIndex={-1}
        data-testid="flp-mode-reason-modal"
      >
        <div className={styles.modalHeader}>
          <h3 id="flp-mode-reason-title">
            {isLive ? 'Go live for these clients' : 'Switch to Shadow mode'}
          </h3>
          <button
            type="button"
            className={styles.x}
            aria-label="Close"
            onClick={onCancel}
          >
            ×
          </button>
        </div>
        <div className={styles.section}>
          {isLive ? (
            <>
              <ul
                data-testid="flp-mode-reason-cohort"
                style={{ margin: '0 0 8px', paddingLeft: 18 }}
              >
                {cohortClients.map((c) => (
                  <li
                    key={c.customerId}
                    data-testid={`flp-mode-reason-cohort-${c.customerId}`}
                  >
                    {c.name}
                  </li>
                ))}
              </ul>
              <p
                className={`${styles.muted} ${styles.small}`}
                style={{ margin: '0 0 12px' }}
                data-testid="flp-mode-reason-cohort-count"
              >
                {cohortClients.length} client
                {cohortClients.length === 1 ? '' : 's'} will receive real
                invitations.
              </p>
              <p
                className={`${styles.muted} ${styles.small}`}
                style={{ margin: '0 0 12px' }}
                data-testid="flp-mode-reason-safety"
              >
                {safetyLineFor(safety)}
              </p>
            </>
          ) : (
            <p
              className={`${styles.muted} ${styles.small}`}
              style={{ margin: '0 0 12px' }}
            >
              No real offers, push, or SMS are sent while in Shadow mode.
            </p>
          )}
          <div className={styles.k}>Reason (required)</div>
          <textarea
            aria-label="Reason for mode change"
            data-testid="flp-mode-reason-textarea"
            className={styles.input}
            style={{ width: '100%', marginTop: 6, minHeight: 64 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div
          className={styles.section}
          style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}
        >
          <button
            type="button"
            className={`${styles.btn} ${styles.btnOutline}`}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.btn}
            data-testid="flp-mode-reason-submit"
            disabled={submitDisabled}
            onClick={() => onConfirm(reason.trim())}
          >
            {isLive ? 'Go live' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModeReasonModal;
